import React, { ReactNode } from "react";
import { Icon } from "@me8eon/icons";
import styles from "./ChatMessageBase.module.scss";
import { CopyShareButtons } from "../../../../components/feedback/CopyShareButtons.component";
import { useAiAssistContext } from "../../../../components/ai-assist-refactored/AiAssist.context";
import { EditModeTextarea } from "../../../../components/ai-assist-refactored/action-components/EditModeTextarea";
import { useNavigate } from "react-router-dom";
import { useFeedback } from "@me8eon/feedback";
import { useDoAi } from "@me8eon/react_intent";
import { useUserData } from "@me8eon/react_authentication";
import { useLoadingFlag } from "@me8eon/async";

type MessageVariant = "user" | "assistant";

type Props = {
    content: ReactNode | string
    isLast?: boolean
    variant: MessageVariant
    disableActions?: boolean
    slotProps?: {
        container?: React.HTMLAttributes<HTMLDivElement>
        avatar: React.HTMLAttributes<HTMLDivElement>
        content?: React.HTMLAttributes<HTMLDivElement>
    }
}

const iconByVariant: Record<MessageVariant, ReactNode> = {
    user: <Icon name="user" />,
    assistant: <Icon name="AiIcon" />
};

export const ChatMessageBase = (props: Props) => {
    const { content, variant = "assistant", slotProps, isLast, disableActions } = props;
    const [editQuery, setEditQuery] = React.useState<boolean>(false);
    const recentMsgRef = React.useRef<HTMLDivElement | null>(null);
    const [isLoading] = useLoadingFlag();


    React.useEffect(() => {
        if (!recentMsgRef.current) return;
        if (isLoading) {
            const element = recentMsgRef.current;
            const elementPosition = element.getBoundingClientRect().top + window.scrollY;
            const headerHeight = 134;

            window.scrollTo({
                top: elementPosition - headerHeight,
                behavior: "smooth"
            });
        }
    }, []);

    const { actualMessages } = useAiAssistContext();
    const navigate = useNavigate();
    const feedback = useFeedback();
    const handleAiQuery = useDoAi();
    const { currentRole } = useUserData();

    const setNewQueryInUrl = (newQuery: string) => {
        const searchParams = new URLSearchParams(location.search);
        if (newQuery) {
            searchParams.set("q", newQuery);
        }
        const queryString = searchParams.toString();
        return queryString ? queryString : null;
    };

    const inputSubmit = (nq: string) => {
        setEditQuery(false);
        handleAiQuery(nq);
        navigate(`${location.pathname}?${setNewQueryInUrl(nq)}`, { replace: true });
        feedback({ source: "query", page: "simple", query: nq, role: currentRole || "unknown" });
    };

    return (
        <div className={`${styles.chatMessage} ${styles[`chatMessage__${variant}`]}`} {...(slotProps?.container || {})}>
            <div ref={isLast && variant === "user" ? recentMsgRef : null} className={styles.chatMessage__avatar} {...(slotProps?.avatar || {})}>
                {iconByVariant[variant]}
            </div>
            <div
                className={`${styles.chatMessage__content} ${styles[`chatMessage__content__${variant}`]}`}
                {...(slotProps?.content || {})}
            >
                {(variant === "assistant" || (variant === "user" && !editQuery)) && <>{content}</>}
                {variant === "user" && editQuery && (
                    <div style={{ width: "100%" }}>
                        <EditModeTextarea
                            defaultVal={content as string}
                            onSend={(newQuery) => inputSubmit(newQuery)}
                            toggleEdit={() => setEditQuery(!editQuery)}
                        />
                    </div>
                )}

                {variant === "user" && (
                    <button onClick={() => setEditQuery(!editQuery)} style={{ padding: 0, background: "transparent", border: 0 }}>
                        <Icon name="edit" />
                    </button>
                )}

                {isLast && variant === "assistant" && !disableActions && (
                    <CopyShareButtons
                        contentToCopy={(actualMessages[actualMessages.length - 1]?.content as string) || ""}
                        messagesToShare={actualMessages}
                    />
                )}
            </div>
        </div>
    );
};